// ===== Packages =====

import moment                           from 'moment-timezone';

// ===== Services =====

import formatTimeToFutureTimestamp      from './formatTimeToFutureTimestamp';

// ===== Interfaces =====

import type { IReleaseTimestampLabel }  from '../interfaces/IReleaseTimestampLabel';

const RELEASE_DISPLAY_TIME_ZONE = 'America/New_York';
const RELEASE_DISPLAY_TIME_ZONE_LABEL = 'Eastern Time';
const EXACT_RELEASE_CUTOFF_WEEKS = 4;

/**
 * Formats the exact time portion of a release label.
 */
const formatExactReleaseTime = (releaseMoment: moment.Moment): string => (
    releaseMoment.minutes() === 0
        ? releaseMoment.format('hA')
        : releaseMoment.format('h:mmA')
);

/**
 * Formats unreleased post, chapter, and section timestamps.
 *
 * Releases more than four weeks away are shown as a fixed Eastern Time date.
 * Nearer releases keep the shorter relative countdown wording.
 */
const formatReleaseTimestamp = (futureTimestamp: number): IReleaseTimestampLabel => {
    const viewerTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = moment().tz(viewerTimeZone);
    const future = moment(futureTimestamp).tz(viewerTimeZone);
    const diff = future.diff(now);

    if (diff <= 0) {
        return {
            isExactDate: false,
            text: 'already happened',
        };
    }

    if (future.diff(now, 'weeks', true) > EXACT_RELEASE_CUTOFF_WEEKS) {
        const easternRelease = moment.tz(futureTimestamp, RELEASE_DISPLAY_TIME_ZONE);

        return {
            isExactDate: true,
            text: `${easternRelease.format('dddd, MMMM Do, YYYY')}, ${formatExactReleaseTime(easternRelease)} ${RELEASE_DISPLAY_TIME_ZONE_LABEL}`,
        };
    }

    return {
        isExactDate: false,
        text: formatTimeToFutureTimestamp(futureTimestamp),
    };
};

export default formatReleaseTimestamp;
